import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  getAllAssignmentsForAdmin,
  getAllProjectsForAdmin,
  getModulesByProject,
  getTasksByModule,
  addAssignment,
  updateAssignment,
  deleteAssignment,
} from "../../service/project_service";
import { getAllEmployees } from "../../service/manager_service";
import DataTable from "../../components/custom_data_table";
import CustomLoadingindicator from "../../components/custom_loading";
import {
  Edit,
  AlertCircle,
  Eye,
  X,
  User2,
  Folder,
  Layers,
  Calendar,
  Clock,
  MessageSquare,
  Trash2,
  RotateCcw,
  Send,
} from "lucide-react";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";
import { toast } from "react-toastify";
import "./AssignedProjects.css";

function AssignedProjects() {
  const [loading, setLoading] = useState(true);
  const [projectAssignments, setProjectAssignments] = useState([]);

  // Form Lists
  const [employeesList, setEmployeesList] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [tasksList, setTasksList] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Form State
  const [isUpdate, setIsUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    assignmentid: "",
    employeeid: "",
    projectid: "",
    moduleid: "",
    projecttaskid: "",
    assigneddate: "",
    deadlinedate: "",
    priority: "Medium",
    remarks: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assignRes, empRes, projRes] = await Promise.all([
        getAllAssignmentsForAdmin(),
        getAllEmployees(),
        getAllProjectsForAdmin(),
      ]);

      if (assignRes.status === "success")
        setProjectAssignments(assignRes.data || []);
      if (empRes.status === "success") setEmployeesList(empRes.data || []);
      if (projRes.status === "success") setProjectsList(projRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load management data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Project Change to load Modules
  useEffect(() => {
    const loadModules = async () => {
      if (!assignmentForm.projectid) {
        setModulesList([]);
        return;
      }
      setLoadingModules(true);
      try {
        const res = await getModulesByProject(assignmentForm.projectid);
        if (res.status === "success") {
          setModulesList(res.data || []);
        }
      } catch (error) {
        console.error("Error fetching modules:", error);
      } finally {
        setLoadingModules(false);
      }
    };
    loadModules();
  }, [assignmentForm.projectid]);

  // Handle Module Change to load Tasks
  useEffect(() => {
    const loadTasks = async () => {
      if (!assignmentForm.moduleid) {
        setTasksList([]);
        return;
      }
      setLoadingTasks(true);
      try {
        const res = await getTasksByModule(assignmentForm.moduleid);
        if (res.status === "success" || res.status === true) {
          setTasksList(res.data || []);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadTasks();
  }, [assignmentForm.moduleid]);

  // Flatten assignments with project context for a single table
  const allAssignmentsFlat = useMemo(() => {
    const flat = [];
    projectAssignments.forEach((project) => {
      if (project.assignments) {
        project.assignments.forEach((assignment) => {
          flat.push({
            ...assignment,
            projectid: project.projectid,
            projectname: project.projectname,
          });
        });
      }
    });
    // Add S.No
    return flat.map((item, index) => ({ ...item, sno: index + 1 }));
  }, [projectAssignments]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      // Lowercase priority for API
      const payload = {
        ...assignmentForm,
        priority: assignmentForm.priority.toLowerCase(),
      };

      const response = isUpdate
        ? await updateAssignment(payload)
        : await addAssignment(payload);

      if (response.status === "success") {
        toast.success(isUpdate ? "Assignment updated!" : "Assignment created!");
        resetForm();
        fetchData();
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setAssignmentForm({
      assignmentid: "",
      employeeid: "",
      projectid: "",
      moduleid: "",
      projecttaskid: "",
      assigneddate: "",
      deadlinedate: "",
      priority: "Medium",
      remarks: "",
    });
    setIsUpdate(false);
  };

  const formRef = useRef(null);

  const handleEdit = (assignment, projectid) => {
    setAssignmentForm({
      ...assignment,
      projectid: projectid,
    });
    setIsUpdate(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);


  const handleView = (assignment) => {
    setViewData(assignment);
    setShowViewModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId || deleteLoading) return;
    setDeleteLoading(true);
    try {
      const res = await deleteAssignment(deleteId);
      if (res.status === "success") {
        toast.success("Assignment deleted");
        fetchData();
        setShowDeleteModal(false);
        setDeleteId(null);
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete assignment");
    } finally {
      setDeleteLoading(false);
    }
  };

  const assignmentColumns = [
    { key: "sno", label: "S.No" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div style={{ display: "flex", gap: "0px" }}>
          <button
            className="action-icn-btn view"
            onClick={() => handleView(row)}
            title="View Assignment"
          >
            <Eye size={16} color="#6366f1" />
          </button>
          <button
            className="action-icn-btn edit"
            onClick={() => handleEdit(row, row.projectid)}
            title="Edit Assignment"
          >
            <Edit size={16} color="#059669" />
          </button>
        </div>
      ),
    },
    {
      key: "employeename",
      label: "Employee",
      render: (val) => <span className="employee-name-cell">{val}</span>,
    },
    { key: "projectname", label: "Project Name" },
    {
      key: "modulename",
      label: "Module Name",
      render: (val) => <span>{val || "-"}</span>,
    },
    {
      key: "taskname",
      label: "Task Name",
      render: (val) => <span>{val || "-"}</span>,
    },
    {
      key: "assigneddate",
      label: "Assigned",
      render: (val) =>
        val ? new Date(val).toLocaleDateString("en-GB") : "N/A",
    },
    {
      key: "deadlinedate",
      label: "Deadline",
      render: (val) =>
        val ? new Date(val).toLocaleDateString("en-GB") : "N/A",
    },
    {
      key: "priority",
      label: "Priority",
      render: (val) => (
        <span
          className={`assignment-badge priority-${val?.toLowerCase() || "medium"}`}
        >
          {val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : "-"}
        </span>
      ),
    },
    {
      key: "remarks",
      label: "Remarks",
      render: (val) => <span className="remarks-cell">{val || "-"}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (val) => (
        <span
          className={`assignment-badge status-${val?.toLowerCase()?.replace(" ", "-") || "pending"}`}
        >
          {val || "Pending"}
        </span>
      ),
    },
  ];

  if (loading && projectAssignments.length === 0)
    return <CustomLoadingindicator />;

  return (
    <div className="assigned-projects-container">
      <h2 ref={formRef}>
        {isUpdate ? "Modify Assignment" : "Assign New Resource"}
      </h2>
      <div style={{ height: 20 }}></div>
      {/* Assignment Form Card */}
      <div className="project-form-card assignment-main-card">
        <form className="crm-form" onSubmit={handleFormSubmit}>
          <div className="assignment-fields-grid">
            <div className="report-form-group">
              <CustomSearchableSelect
                label="Employee Selection *"
                placeholder="Select employee"
                value={assignmentForm.employeeid}
                required={true}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    employeeid: e.target.value,
                  })
                }
                options={employeesList.map((emp) => ({
                  label: emp.employeename,
                  value: emp.employeeid,
                }))}
              />
            </div>
            <div className="report-form-group">
              <CustomSearchableSelect
                label="Project Selection *"
                placeholder="Select project"
                value={assignmentForm.projectid}
                required={true}
                onChange={(e) => {
                  setAssignmentForm({
                    ...assignmentForm,
                    projectid: e.target.value,
                    moduleid: "", // Reset module when project changes
                  });
                }}
                options={projectsList.map((p) => ({
                  label: p.projectname,
                  value: p.projectid,
                }))}
              />
            </div>
            <div className="report-form-group">
              <CustomSearchableSelect
                label="Module Selection *"
                placeholder={
                  loadingModules ? "Loading task Clusters..." : "Select module"
                }
                value={assignmentForm.moduleid}
                required={true}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    moduleid: e.target.value,
                  })
                }
                options={modulesList.map((m) => ({
                  label: m.modulename,
                  value: m.moduleid,
                }))}
              />
            </div>
            <div className="report-form-group">
              <CustomSearchableSelect
                label="Task Selection *"
                placeholder={
                  loadingTasks ? "Loading tasks..." : "Select task"
                }
                value={assignmentForm.projecttaskid}
                required={true}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    projecttaskid: e.target.value,
                  })
                }
                options={tasksList.map((t) => ({
                  label: t.taskname,
                  value: t.projecttaskid,
                }))}
                disabled={!assignmentForm.moduleid}
              />
            </div>
            <div className="report-form-group">
              <label>Priority Level *</label>
              <select
                className="styled-select"
                value={assignmentForm.priority}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    priority: e.target.value,
                  })
                }
                required
              >
                <option value="">Select Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="report-form-group">
              <label>Assigned Date *</label>
              <div className="input-with-icon">
                <Calendar size={18} className="field-icon" />
                <input
                  type="date"
                  value={assignmentForm.assigneddate}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      assigneddate: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>
            <div className="report-form-group">
              <label>Deadline Date *</label>
              <div className="input-with-icon">
                <Calendar size={18} className="field-icon" />
                <input
                  type="date"
                  value={assignmentForm.deadlinedate}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      deadlinedate: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>
            <div className="report-form-group full-width">
              <label>Assignment Remarks </label>
              <div className="input-with-icon-tarea">
                <textarea
                  placeholder="Provide detailed instructions or success criteria for this assignment..."
                  rows="3"
                  value={assignmentForm.remarks}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      remarks: e.target.value,
                    })
                  }

                ></textarea>
              </div>
            </div>
          </div>

          <div className="form-actions-centered">
            <button
              type="button"
              className="crm-btn-secondary"
              onClick={resetForm}
              disabled={isSubmitting}
            >
              <RotateCcw size={18} style={{ marginRight: "8px" }} />
              {isUpdate ? "Cancel" : "Reset"}
            </button>
            <button
              type="submit"
              className="crm-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                "Processing..."
              ) : (
                <>
                  <Send size={18} style={{ marginRight: "8px" }} />
                  {isUpdate ? "Update" : " Submit"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* List Section */}
      <div
        className="assignment-overview-section"
        style={{ marginTop: "50px" }}
      >
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          Assigned Projects
        </h3>

        {allAssignmentsFlat.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} color="#94a3b8" />
            <h3>No Assignments Found</h3>
            <p>
              Begin by filling out the deployment form above to allocate
              resources.
            </p>
          </div>
        ) : (
          <DataTable
            columns={assignmentColumns}
            data={allAssignmentsFlat}
            pageSize={10}
            loading={loading}
          />
        )}
      </div>
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(3px)",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "32px 36px",
              borderRadius: "16px",
              minWidth: "320px",
              maxWidth: "340px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              textAlign: "center",
              fontFamily: "Inter, sans-serif",
              animation:
                "fadeInScaleAdmin 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "24px",
                color: "#ef4444",
                boxShadow: "0 4px 10px rgba(239, 68, 68, 0.15)",
              }}
            >
              <Trash2 size={24} />
            </div>
            <h3
              style={{
                margin: "0 0 10px 0",
                color: "#0f172a",
                fontSize: "20px",
                fontWeight: "700",
                letterSpacing: "-0.01em",
              }}
            >
              Delete Assignment?
            </h3>
            <p
              style={{
                color: "#64748b",
                margin: "0 0 28px 0",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              Are you sure you want to remove this project assignment? This
              action cannot be undone.
            </p>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "12px" }}
            >
              <button
                onClick={() => !deleteLoading && setShowDeleteModal(false)}
                disabled={deleteLoading}
                style={{
                  padding: "10px 24px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "10px",
                  background: "#ffffff",
                  color: "#475569",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
              >
                Keep it
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.25)",
                }}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
            <style>{`@keyframes fadeInScaleAdmin { from { opacity:0; transform: scale(0.9); } to { opacity:1; transform: scale(1); } }`}</style>
          </div>
        </div>
      )}
      {/* View Detail Modal */}
      {showViewModal && viewData && (
        <div
          className="premium-dialog-overlay"
          onClick={() => setShowViewModal(false)}
        >
          <div
            className="premium-view-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-premium">
              <div className="header-title-group">
                <div className="header-icon-badge">
                  <Layers size={20} />
                </div>
                <h5>Assignment Details</h5>
              </div>
              <button
                className="modal-close-circle"
                onClick={() => setShowViewModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body-scrollable">
              <div className="detail-section-card">
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <User2 size={14} />
                    <span>Employee</span>
                  </div>
                  <div className="detail-value-text">
                    {viewData.employeename}
                  </div>
                </div>
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Folder size={14} />
                    <span>Project</span>
                  </div>
                  <div className="detail-value-text">
                    {viewData.projectname}
                  </div>
                </div>
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Layers size={14} />
                    <span>Module</span>
                  </div>
                  <div className="detail-value-text">{viewData.modulename}</div>
                </div>
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Layers size={14} />
                    <span>Task</span>
                  </div>
                  <div className="detail-value-text">{viewData.taskname || "Full Module Assignment"}</div>
                </div>
              </div>

              <div className="detail-grid-layout">
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Calendar size={14} />
                    <span>Assigned Date</span>
                  </div>
                  <div className="detail-value-text">
                    {viewData.assigneddate
                      ? new Date(viewData.assigneddate).toLocaleDateString(
                        "en-GB",
                      )
                      : "N/A"}
                  </div>
                </div>
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Clock size={14} />
                    <span>Deadline</span>
                  </div>
                  <div
                    className="detail-value-text"
                    style={{ color: "#e11d48" }}
                  >
                    {viewData.deadlinedate
                      ? new Date(viewData.deadlinedate).toLocaleDateString(
                        "en-GB",
                      )
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="detail-section-card">
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <AlertCircle size={14} />
                    <span>Priority Status</span>
                  </div>
                  <div
                    className={`status-pill-badge priority-${(viewData.priority || "Medium").toLowerCase()}`}
                  >
                    <span className="indicator-dot"></span>
                    {viewData.priority}
                  </div>
                </div>
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <MessageSquare size={14} />
                    <span>Manager Remarks</span>
                  </div>
                  <div className="remark-surface-box">
                    {viewData.remarks ||
                      "No additional remarks provided for this assignment."}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer-wrapper">
              <button
                className="modal-action-btn"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignedProjects;
